// WhatsApp GROUP helpers — the frontend half of the cross-repo group contract.
//
// Naming: everything here is `waGroup*` on purpose. "Group" alone is already taken twice in
// this codebase (the backend's `metadata.group` thread-label catalog and the thread-list
// "Groups" color folders), so an unqualified name would read as either of those. A WhatsApp
// group is a third, unrelated thing: a real multi-participant chat bridged from WhatsApp,
// discriminated by `metadata.conversationType === "whatsapp_group"` on threads and by the
// `@g.us` JID suffix on per-message metadata.

/** The Messages-page lens id for the WhatsApp Groups tab (see VALID_THREAD_LENSES). */
export const WA_GROUPS_LENS = "waGroups";

/**
 * Whether a thread row is a bridged WhatsApp group chat. `metadata.conversationType` is the
 * backend-derived source of truth (stamped from the JID shape at ensure time); enriched list
 * rows additionally carry a top-level `isWhatsappGroup` flag (ThreadDetails), accepted here so
 * callers holding only the enriched row — before metadata is loaded — classify identically.
 */
export function isWaGroupThread(thread) {
  if (!thread || typeof thread !== "object") return false;
  if (thread.isWhatsappGroup === true) return true;
  return (
    String(thread?.metadata?.conversationType || "").toLowerCase() === "whatsapp_group"
  );
}

/**
 * The WhatsApp group JID (`<id>@g.us`) a thread is bridged to — the identity that is IDENTICAL
 * across every clinic gateway that is a member of the same group, and therefore the merge key
 * behind the merged Groups view. Returns "" for anything that is not a group thread.
 *
 * The `@g.us` suffix check is LOAD-BEARING, not cosmetic. `metadata.customThreadId` is also
 * where 1:1 WhatsApp threads store the PATIENT'S PHONE (the backend keys those threads by
 * phone / external id), so reading it unguarded would hand the merger a key that collapses
 * unrelated patient conversations into one row. The server-side twin of this check is
 * `whatsappphone.IsGroupJID`.
 */
export function waGroupJid(thread) {
  const raw = String(thread?.metadata?.customThreadId || "").trim().toLowerCase();
  return raw.endsWith("@g.us") ? raw : "";
}

/**
 * Whether a WS/history message originated in a WhatsApp group. The wire discriminator is the
 * group JID in `metadata.whatsapp.remoteJid` (`<id>@g.us`) — present on every bridged group
 * message regardless of direction, unlike thread metadata which the message may arrive before.
 */
export function isWaGroupMessage(message) {
  return String(message?.metadata?.whatsapp?.remoteJid || "")
    .toLowerCase()
    .endsWith("@g.us");
}

/**
 * The group roster snapshot (`metadata.whatsappGroup`), normalized so callers never
 * null-check: missing/partial metadata yields empty strings, an empty participants array and a
 * zero count. `participantCount` prefers the server-computed field but falls back to the
 * roster length — the two can briefly disagree while a roster sync is in flight.
 */
export function getWaGroupInfo(thread) {
  const raw = thread?.metadata?.whatsappGroup;
  const group = raw && typeof raw === "object" ? raw : {};
  const participants = Array.isArray(group.participants)
    ? group.participants.filter((p) => p && typeof p === "object")
    : [];
  const serverCount = Number(group.participantCount);
  return {
    subject: String(group.subject || ""),
    owner: String(group.owner || ""),
    participants,
    participantCount:
      Number.isFinite(serverCount) && serverCount > 0 ? serverCount : participants.length,
    // Announce-only ("admins can send") groups — the composer disables on this.
    announce: group.announce === true,
    syncedAt: group.syncedAt || null,
  };
}

/** The sending participant's JID on a group message ("" for 1:1 / non-WhatsApp messages). */
export function getWaGroupSenderJid(message) {
  return String(message?.metadata?.whatsapp?.senderJid || "").trim();
}

// Group system messages (contentType "group.system") carry a structured body:
// { event, text, actorName, targetNames, subject, ... }. The body may arrive as an object or
// as its JSON-serialized form depending on the transport path — accept both.
function waGroupSystemBody(message) {
  let body = message?.body;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      // A plain-string body IS the display text — surface it via the fallback below.
      return { text: body };
    }
  }
  return body && typeof body === "object" ? body : {};
}

// Oxford-free name list ("A", "A and B", "A, B and C") for system-line targets.
function joinNames(names) {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * English one-liner for a group system message ("X added Y", "X changed the subject…").
 * Events outside the known vocabulary — and bodies without an event at all — fall back to the
 * bot-provided `body.text`, so a newer bot never renders as a blank line here.
 */
export function formatWaGroupSystemLine(message) {
  const body = waGroupSystemBody(message);
  const fallback = String(body.text || "").trim();
  const actor = String(body.actorName || "").trim() || "Someone";
  const targetNames = (Array.isArray(body.targetNames) ? body.targetNames : [])
    .map((n) => String(n || "").trim())
    .filter(Boolean);
  const targets = joinNames(targetNames) || "a participant";
  const subject = String(body.subject || "").trim();
  switch (String(body.event || "").toLowerCase()) {
    case "create":
      return subject ? `${actor} created the group "${subject}"` : `${actor} created the group`;
    case "participant_add":
      return `${actor} added ${targets}`;
    case "participant_remove":
      return `${actor} removed ${targets}`;
    case "participant_leave":
      // Leaving is self-inflicted: the actor is the leaver (targets, when present, agree).
      return `${joinNames(targetNames) || actor} left`;
    case "participant_promote":
      return `${actor} made ${targets} an admin`;
    case "participant_demote":
      return `${actor} removed ${targets} as admin`;
    case "subject_change":
      return subject
        ? `${actor} changed the subject to "${subject}"`
        : `${actor} changed the subject`;
    default:
      return fallback;
  }
}

/**
 * Identity-key → display-name map for a group thread: reaction authors, receipt rows and the
 * roster panel all resolve through it. Keys are lowercased and every identity is indexed under
 * ALL of its forms (full jid, bare lid + `<lid>@lid`, bare phone + `<phone>@s.whatsapp.net`)
 * because the roster stores bare local parts while wire values carry full jids.
 *
 * Three first-wins tiers: the roster's explicit contact name, then the name the conversation
 * established for the sender (whatever `resolveSenderName(message)` returns — the same identity
 * the bubbles render), then the phone/jid fallback so an unnamed participant still resolves to
 * something a human can dial. The roster links the lid and phone forms of one person, and in
 * lid-addressed groups the roster keys a participant by lid while their messages arrive
 * phone-keyed — so each participant's best name is propagated across ALL their forms, letting
 * either side resolve names the other side learned.
 */
export function buildWaGroupNamesByJid(waGroupInfo, historyItems, resolveSenderName) {
  if (!waGroupInfo) return null;
  const participants = Array.isArray(waGroupInfo.participants) ? waGroupInfo.participants : [];
  const keyFormsOf = (jid, lid, phone) => {
    const forms = [];
    const push = (value) => {
      const key = String(value || "").trim().toLowerCase();
      if (key && !forms.includes(key)) forms.push(key);
    };
    push(jid);
    const lidLocal = String(lid || "").trim().toLowerCase().split("@")[0];
    if (lidLocal) {
      push(lidLocal);
      push(`${lidLocal}@lid`);
    }
    const phoneLocal = String(phone || "").trim().toLowerCase().split("@")[0];
    if (phoneLocal) {
      push(phoneLocal);
      push(`${phoneLocal}@s.whatsapp.net`);
    }
    return forms;
  };
  const putAll = (map, forms, name) => {
    if (!name) return;
    for (const key of forms) {
      if (!map[key]) map[key] = name;
    }
  };
  // Candidate names by key: roster contact names, then conversation-established names
  // (newest message wins — iterate newest-first under first-wins).
  const candidates = {};
  for (const p of participants) {
    putAll(candidates, keyFormsOf(p?.jid, p?.lid, p?.phoneNumber), String(p?.displayName || "").trim());
  }
  const items = Array.isArray(historyItems) ? historyItems : [];
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const msg = items[i];
    if (!isWaGroupMessage(msg)) continue;
    const wa = msg?.metadata?.whatsapp || {};
    const name = String(resolveSenderName?.(msg) || "").trim();
    if (!name) continue;
    putAll(candidates, keyFormsOf(wa.senderJid, wa.lid, wa.phoneNumber), name);
  }
  // Final map: each participant's best name across ALL their forms, then candidate entries for
  // senders no longer in the roster, then the phone/jid fallback tier.
  const out = {};
  for (const p of participants) {
    const forms = keyFormsOf(p?.jid, p?.lid, p?.phoneNumber);
    const best =
      String(p?.displayName || "").trim() || forms.map((key) => candidates[key]).find(Boolean) || "";
    putAll(out, forms, best);
  }
  for (const [key, name] of Object.entries(candidates)) {
    if (!out[key]) out[key] = name;
  }
  for (const p of participants) {
    const forms = keyFormsOf(p?.jid, p?.lid, p?.phoneNumber);
    putAll(out, forms, String(p?.phoneNumber || "").trim() || (forms[0] || "").split("@")[0]);
  }
  return out;
}

/**
 * Roster copy for display: participants without a contact name pick up the
 * conversation-established name from the map. Only a REAL name is written back — a lookup that
 * falls through to the phone/jid fallback tier leaves the entry nameless, so row rendering
 * keeps its own phone fallback (no phone-as-name dupes).
 */
export function enrichWaGroupRosterNames(waGroupInfo, namesByJid) {
  if (!waGroupInfo || !namesByJid) return waGroupInfo;
  const source = Array.isArray(waGroupInfo.participants) ? waGroupInfo.participants : [];
  const participants = source.map((p) => {
    if (String(p?.displayName || "").trim()) return p;
    const jid = String(p?.jid || "").trim().toLowerCase();
    const lidLocal = String(p?.lid || "").trim().toLowerCase().split("@")[0];
    const phoneLocal = String(p?.phoneNumber || "").trim().toLowerCase().split("@")[0];
    const keys = [
      jid,
      lidLocal,
      lidLocal ? `${lidLocal}@lid` : "",
      phoneLocal,
      phoneLocal ? `${phoneLocal}@s.whatsapp.net` : "",
    ].filter(Boolean);
    const identityEchoes = new Set([jid.split("@")[0], lidLocal, phoneLocal].filter(Boolean));
    let resolved = "";
    for (const key of keys) {
      const hit = String(namesByJid[key] || "").trim();
      if (hit && !identityEchoes.has(hit.toLowerCase())) {
        resolved = hit;
        break;
      }
    }
    return resolved ? { ...p, displayName: resolved } : p;
  });
  return { ...waGroupInfo, participants };
}
